import React, { useEffect, useState } from "react";
import { Row, Col, Form, ListGroup, Button, Badge, Alert, InputGroup } from "react-bootstrap";
import { FaPlus, FaTrash, FaSearch, FaTimes } from "react-icons/fa";
import { permissionGroupService } from "../../services/permissionGroup.service";
import { permissionGroupRunbookService } from "../../services/permissionGroupRunbook.service";
import { runbookService } from "../../services/runbook.service";

function GroupRunbooksTab({ user, preSelectedGroup }) {
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(preSelectedGroup || null);
  const [groupRunbooks, setGroupRunbooks] = useState([]);
  const [availableRunbooks, setAvailableRunbooks] = useState([]);
  const [allRunbooks, setAllRunbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredAvailableRunbooks, setFilteredAvailableRunbooks] = useState([]);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (preSelectedGroup) {
      setSelectedGroup(preSelectedGroup);
    }
  }, [preSelectedGroup]);

  useEffect(() => {
    if (selectedGroup && allRunbooks.length > 0) {
      loadGroupRunbooks(selectedGroup.groupId);
    }
  }, [selectedGroup, allRunbooks]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = availableRunbooks.filter(
        (runbook) =>
          runbook.runbookName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          runbook.runbookDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          runbook.runbookId?.toString().includes(searchTerm)
      );
      setFilteredAvailableRunbooks(filtered);
    } else {
      setFilteredAvailableRunbooks(availableRunbooks);
    }
  }, [searchTerm, availableRunbooks]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const params = {
        clientId: user.clientId,
        clientGuid: user.clientGuid,
      };

      const groupsResp = await permissionGroupService.getAll(params);
      const groupsData = groupsResp.data || groupsResp;

      // Load all runbooks for this client
      const runbooksParams = {
        ...params,
        max_results: 1000,
        current_page: 0,
      };
      const runbooksResp = await runbookService.get(runbooksParams);
      const runbooksData = runbooksResp.data?.data || runbooksResp.data || runbooksResp;

      setGroups(groupsData);
      setAllRunbooks(runbooksData);
      setError(null);
    } catch (err) {
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const loadGroupRunbooks = async (groupId) => {
    if (!groupId) {
      return;
    }

    try {
      const response = await permissionGroupRunbookService.getRunbooksForGroup(groupId);
      const groupRbs = response.data || response;

      setGroupRunbooks(groupRbs);

      // Calculate available runbooks (not yet assigned to group)
      const assignedRunbookIds = groupRbs.map(gr => gr.runbookId);
      const available = allRunbooks.filter(r => !assignedRunbookIds.includes(r.runbookId));
      setAvailableRunbooks(available);
      setFilteredAvailableRunbooks(available);
    } catch (err) {
      setError("Failed to load group runbooks");
    }
  };

  const handleAssignRunbook = async (runbookId) => {
    try {
      await permissionGroupRunbookService.assign({
        groupId: selectedGroup.groupId,
        runbookId: runbookId,
      });
      loadGroupRunbooks(selectedGroup.groupId);
    } catch (err) {
      setError("Failed to assign runbook");
    }
  };

  const handleRemoveRunbook = async (runbookId) => {
    try {
      await permissionGroupRunbookService.remove(selectedGroup.groupId, runbookId);
      loadGroupRunbooks(selectedGroup.groupId);
    } catch (err) {
      setError("Failed to remove runbook");
    }
  };

  const getRunbookDetails = (runbookId) => {
    return allRunbooks.find(r => r.runbookId === runbookId);
  };

  if (loading) return <div>Loading...</div>;

  return (
    <>
      {error && <Alert variant="danger">{error}</Alert>}

      <Row>
        {!preSelectedGroup && (
          <Col md={4}>
            <h5>Select Permission Group</h5>
            <ListGroup>
              {groups.filter(g => g.active).map((group) => (
                <ListGroup.Item
                  key={group.groupId}
                  active={selectedGroup?.groupId === group.groupId}
                  onClick={() => setSelectedGroup(group)}
                  style={{ cursor: "pointer" }}
                >
                  {group.groupName}
                  <br />
                  <small className="text-muted">{group.groupDescription}</small>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Col>
        )}

        <Col md={preSelectedGroup ? 12 : 8}>
          {selectedGroup ? (
            <>
              {!preSelectedGroup && (
                <h5>
                  Runbooks for: {selectedGroup.groupName}
                  <Badge bg="secondary" className="ms-2">
                    {groupRunbooks.length}
                  </Badge>
                </h5>
              )}

              <Row>
                <Col md={6}>
                  <h6>Available Runbooks</h6>
                  <InputGroup className="mb-2">
                    <InputGroup.Text>
                      <FaSearch />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search runbooks..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <Button variant="outline-secondary" onClick={() => setSearchTerm("")}>
                        <FaTimes />
                      </Button>
                    )}
                  </InputGroup>
                  <ListGroup style={{ height: "600px", overflowY: "auto" }}>
                    {filteredAvailableRunbooks.length === 0 ? (
                      <p className="text-muted p-3">
                        {searchTerm ? "No runbooks match your search" : "All runbooks have been assigned"}
                      </p>
                    ) : (
                      filteredAvailableRunbooks.map((runbook) => (
                        <ListGroup.Item
                          key={runbook.runbookId}
                          className="d-flex justify-content-between align-items-center"
                        >
                          <div>
                            <strong>{runbook.runbookName}</strong>
                            <br />
                            <small className="text-muted">
                              {runbook.runbookDescription || "No description"}
                            </small>
                          </div>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleAssignRunbook(runbook.runbookId)}
                          >
                            <FaPlus />
                          </Button>
                        </ListGroup.Item>
                      ))
                    )}
                  </ListGroup>
                </Col>

                <Col md={6}>
                  <h6>Assigned Runbooks</h6>
                  <ListGroup style={{ height: "600px", overflowY: "auto", marginTop: "48px" }}>
                    {groupRunbooks.length === 0 ? (
                      <p className="text-muted p-3">No runbooks assigned to this group</p>
                    ) : (
                      groupRunbooks.map((gr) => {
                        // Backend now returns runbookName and runbookDescription directly
                        // Use those if available, otherwise fall back to lookup
                        const runbookDetails = getRunbookDetails(gr.runbookId);
                        const name = gr.runbookName || runbookDetails?.runbookName || gr.runbookId;
                        const description = gr.runbookDescription || runbookDetails?.runbookDescription || "No description";

                        return (
                          <ListGroup.Item
                            key={gr.runbookId}
                            className="d-flex justify-content-between align-items-center"
                          >
                            <div>
                              <strong>{name}</strong>
                              <br />
                              <small className="text-muted">
                                {description}
                              </small>
                            </div>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleRemoveRunbook(gr.runbookId)}
                            >
                              <FaTrash />
                            </Button>
                          </ListGroup.Item>
                        );
                      })
                    )}
                  </ListGroup>
                </Col>
              </Row>
            </>
          ) : (
            <p className="text-muted">Select a permission group to manage its runbooks</p>
          )}
        </Col>
      </Row>
    </>
  );
}

export default GroupRunbooksTab;
